import React, { useState } from 'react';
import { copyToClipboard } from '@/shared/utils/exportUtils';

const SENTRY_ORG = "zapt-apps";
const VERCEL_TEAM = "zapt-ai-platform-apps";

export default function AdminDeploymentSection({ app, appId, showToast }) {
  const appData = app;
  const id = appId;
  const [expandedSections, setExpandedSections] = useState({
    vercel: false,
    sentry: false
  });
  const [copiedCommand, setCopiedCommand] = useState(null);
  const [showPrerequisites, setShowPrerequisites] = useState(false);
  const prodUrl = appData?.appUrl;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const vercelCommands = [
    {
      label: 'Link Vercel Project',
      command: `vercel link --scope ${VERCEL_TEAM} --yes --project ${id}`,
      description: 'Link your local directory to the Vercel project'
    },
    {
      label: 'Pull Environment Variables',
      command: `vercel env pull .env.local`,
      description: 'Download environment variables from Vercel to your local .env.local file'
    },
    {
      label: 'Run Local Dev',
      command: `npm run dev`,
      description: 'Run app locally (frontend only)'
    }
  ];

  const handleCopy = async (command, label) => {
    const result = await copyToClipboard(command);
    if (result.success) {
      setCopiedCommand(label);
      if (showToast) {
        showToast(`Copied: ${command}`, 'success', 2000);
      }
      setTimeout(() => setCopiedCommand(null), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy command', 'error');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>🚀</span>
          <span>Deployment & Service Configuration</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          View production URL and service configuration details
        </p>
      </div>

      {/* Production URL Section */}
      {prodUrl && (
        <div className="mb-6">
          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-green-900">Production URL</p>
              <a
                href={prodUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-green-700 hover:text-green-800"
              >
                {prodUrl} →
              </a>
              {appData?.liveSince && (
                <p className="text-xs text-green-600 mt-1">
                  Live since {new Date(appData.liveSince).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Service Configuration Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Service Configuration</h3>
          <p className="text-xs text-gray-500 mb-4">
            These services are optional. If you use any of them, structure them as detailed below. App ID ({id}) is used as the identifier.
          </p>
        </div>

        {/* Vercel Configuration */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('vercel')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Vercel</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.vercel ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.vercel && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              {/* Log Drain Info */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Error Logging Enabled</p>
                    <p className="text-xs text-blue-700 mt-1">
                      All apps in this organisation have a log drain configured to automatically send error logs to ZAPT. 
                      You'll receive email notifications when errors occur, with direct links to investigate in Vercel.
                    </p>
                  </div>
                </div>
              </div>

              {/* Vercel Authentication Reminder */}
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-900">Deployment Protection</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Don't forget to go to Settings &gt; Deployment Protection &gt; Vercel Authentication and then set it to Disabled.
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      This lets the outside world view the app
                    </p>
                  </div>
                </div>
              </div>

              {/* Node.js v24 url.parse() warning */}
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-900 mb-2">
                  Node.js v24 <code className="text-xs bg-amber-100 px-1 py-0.5 rounded">url.parse()</code> Deprecation Warning
                </h5>
                <p className="text-xs text-gray-600 mb-2">
                  This warning comes from <strong>third-party SDKs</strong> (e.g., Stytch). If you need to avoid it, pin Node.js to v22 in <code className="bg-amber-100 px-1 py-0.5 rounded">package.json</code>:
                </p>
                <pre className="text-xs text-gray-800 font-mono bg-white px-3 py-2 rounded border border-amber-200 overflow-x-auto">
{`"engines": {
  "node": "22.x"
}`}
                </pre>
                <p className="text-xs text-gray-500 mt-2">
                  Vercel defaults to Node.js 24, which triggers these deprecation warnings from SDKs that haven't updated yet.
                </p>
              </div>

              {/* SPA Fallback Rewrite */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-900 mb-2">
                  SPA Fallback Rewrite in <code className="text-xs bg-blue-100 px-1 py-0.5 rounded">vercel.json</code>
                </h5>
                <p className="text-xs text-gray-600 mb-2">
                  Use a <strong>negative lookahead</strong> so the SPA fallback never matches <code className="bg-blue-100 px-1 py-0.5 rounded">/api/</code> routes:
                </p>
                <pre className="text-xs text-gray-800 font-mono bg-white px-3 py-2 rounded border border-blue-200 overflow-x-auto">
{`"rewrites": [
  { "source": "/((?!api/).*)", "destination": "/index.html" }
]`}
                </pre>
                <p className="text-xs text-gray-600 mt-2 mb-1">
                  <strong>Why not <code className="bg-blue-100 px-1 py-0.5 rounded">{"/(.*)"}</code>?</strong> A blanket catch-all matches everything, including API requests.
                  Once an API request hits that rewrite it goes through Vercel's "rewrite resolution" path instead of normal function routing,
                  so dynamic routes like <code className="bg-blue-100 px-1 py-0.5 rounded">[id].js</code> don't match and you get 405 errors.
                </p>
                <div className="mt-2 text-xs text-gray-600">
                  <p className="mb-1">With the negative lookahead:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-1">
                    <li><code className="bg-blue-100 px-1 rounded">/practitioners</code>, <code className="bg-blue-100 px-1 rounded">/settings</code>, etc. → rewrite to <code className="bg-blue-100 px-1 rounded">index.html</code></li>
                    <li><code className="bg-blue-100 px-1 rounded">/api/...</code> → no rewrite, normal Vercel function routing</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-700">
                <div>
                  <span className="font-medium">Team:</span> {VERCEL_TEAM}
                </div>
                <div>
                  <span className="font-medium">Project Name:</span> {id}
                </div>
                <div>
                  <span className="font-medium">URL:</span>{' '}
                  <a
                    href={`https://vercel.com/${VERCEL_TEAM}/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zapt-purple hover:text-zapt-purple/80"
                  >
                    https://vercel.com/{VERCEL_TEAM}/{id} →
                  </a>
                </div>
                <div className="mt-3">
                  <a
                    href="https://vercel.com/new?teamSlug=zapt-ai-platform-apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-zapt-purple rounded-lg hover:bg-zapt-purple/90 transition-colors"
                  >
                    Create Vercel App →
                  </a>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  The project name matches the App ID ({id}).
                </div>
              </div>

              {/* Local Development Commands */}
              <div className="mt-6 pt-4 border-t border-gray-300">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-gray-900">Local Development</h5>
                  <button
                    onClick={() => setShowPrerequisites(!showPrerequisites)}
                    className="text-xs text-zapt-purple hover:text-zapt-purple/80 flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Prerequisites
                  </button>
                </div>

                {showPrerequisites && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-medium text-amber-900 mb-2">Make sure you have:</p>
                    <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
                      <li>Install Vercel CLI: <code className="bg-amber-100 px-1 rounded">npm i -g vercel</code></li>
                      <li>Login to Vercel: <code className="bg-amber-100 px-1 rounded">vercel login</code></li>
                      <li>Have access to the {VERCEL_TEAM} team</li>
                    </ol>
                  </div>
                )}

                <div className="space-y-3">
                  {vercelCommands.map(({ label, command, description }) => (
                    <div
                      key={label}
                      className="p-3 bg-white border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
                          <p className="text-xs text-gray-500 mb-2">{description}</p>
                          <code className="text-xs text-gray-800 font-mono bg-gray-100 px-2 py-1 rounded border border-gray-200 block break-all">
                            {command}
                          </code>
                        </div>
                        <button
                          onClick={() => handleCopy(command, label)}
                          className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 flex-shrink-0"
                        >
                          {copiedCommand === label ? (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sentry Configuration */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('sentry')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Sentry</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.sentry ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.sentry && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              {/* Step 1: Create Sentry Project */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Step 1: Create Sentry Project</h5>
                <div className="space-y-2 text-sm text-gray-700">
                  <div>
                    <span className="font-medium">Organization:</span> {SENTRY_ORG}
                  </div>
                  <div>
                    <span className="font-medium">Project Slug:</span> {id}
                  </div>
                  <div>
                    <span className="font-medium">URL:</span>{' '}
                    <a
                      href={`https://sentry.io/organizations/${SENTRY_ORG}/projects/${id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zapt-purple hover:text-zapt-purple/80"
                    >
                      https://sentry.io/organizations/{SENTRY_ORG}/projects/{id}/ →
                    </a>
                  </div>
                  <div className="mt-3">
                    <a
                      href={`https://sentry.io/organizations/${SENTRY_ORG}/projects/new/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-zapt-purple rounded-lg hover:bg-zapt-purple/90 transition-colors"
                    >
                      Create Sentry Project →
                    </a>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    The project slug must match the App ID ({id}).
                  </div>
                </div>
              </div>

              {/* Step 2: Create Alert Rule */}
              <div className="pt-4 border-t border-gray-300">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Step 2: Create Alert Rule</h5>
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Connect to ZAPT Error Tracking</p>
                      <p className="text-xs text-blue-700 mt-1 mb-2">
                        We have set up a "ZAPT Apps Error Tracker" integration to receive Sentry events. To enable error notifications:
                      </p>
                      <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                        <li>Click "Create Alert Rule" below</li>
                        <li>In "Then perform these actions", select "Send a notification via an integration"</li>
                        <li>Choose "ZAPT Apps Error Tracker" as the integration</li>
                        <li>Set "Action interval" to 5 minutes</li>
                        <li>Save the alert rule</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <a
                  href={`https://${SENTRY_ORG}.sentry.io/issues/alerts/new/issue/?project=${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-zapt-purple rounded-lg hover:bg-zapt-purple/90 transition-colors"
                >
                  Create Alert Rule →
                </a>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

